import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { dealerApi, DealerMe } from '../services/dealerApi';
import { ApiError } from '../services/api';
import { errorCodeOf } from '../utils/dealer';

/**
 * Spec 085 (§4 Dealer portal) — `useDealer()`: the signed-in dealer user's profile and flags from
 * `GET /api/dealers/me`, loaded once per dealer session and reloaded after the agreement is accepted.
 *
 * `state`:
 *   - `ok`         — an ACTIVE membership in an ACTIVE dealer;
 *   - `featureOff` — 404 FEATURE_OFF (the Runtime Config switch is off, BR-33);
 *   - `blocked`    — 403 DEALER_SUSPENDED / DEALER_NOT_ACTIVE / NOT_A_DEALER_USER (`blockedCode`), or a
 *                    dealer that is not ACTIVE;
 *   - `error`      — anything else (network, 5xx) on the first load. Once loaded, a failed reload keeps
 *                    the page (and any unsaved input); only a 403/404 answer changes the state.
 *
 * The token refreshes every few minutes; that alone never re-fetches the profile (it is keyed on the
 * session, read through a ref).
 */
export type DealerLoadState = 'loading' | 'ok' | 'featureOff' | 'blocked' | 'error';

export interface DealerContextValue {
  token: string | null;
  me: DealerMe | null;
  state: DealerLoadState;
  blockedCode: string | null;
  reload: () => Promise<void>;
  /** Owner (realm role DEALER_OWNER). */
  isOwner: boolean;
  /** Owner, or staff the owner granted canCommitSpend (BR-6). */
  canCommitSpend: boolean;
  /**
   * The wallet switch is on (`me.walletEnabled`): packs, the prepaid balance and assigning from it. Off (or a
   * backend that does not send the field) hides those surfaces and never calls `/balance` or `/packs`.
   */
  walletEnabled: boolean;
}

export const DealerContext = createContext<DealerContextValue | null>(null);

export function DealerProvider({ token, children }: { token: string | null; children: ReactNode }) {
  const [me, setMe] = useState<DealerMe | null>(null);
  const [state, setState] = useState<DealerLoadState>('loading');
  const [blockedCode, setBlockedCode] = useState<string | null>(null);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const loadedRef = useRef(false);

  const reload = useCallback(async () => {
    const token = tokenRef.current;
    if (!token) return;
    try {
      const data = await dealerApi.me(token);
      loadedRef.current = true;
      setMe(data);
      if (data.dealer?.status !== 'ACTIVE') {
        setBlockedCode(data.dealer?.status === 'SUSPENDED' ? 'DEALER_SUSPENDED' : 'DEALER_NOT_ACTIVE');
        setState('blocked');
      } else {
        setBlockedCode(null);
        setState('ok');
      }
    } catch (err) {
      const code = errorCodeOf(err);
      if (err instanceof ApiError && err.status === 404 && code === 'FEATURE_OFF') {
        setState('featureOff');
      } else if (err instanceof ApiError && err.status === 403) {
        setBlockedCode(code);
        setState('blocked');
      } else if (!loadedRef.current) {
        setState('error');
      }
    }
  }, []);

  const signedIn = !!token;
  useEffect(() => {
    if (!signedIn) {
      loadedRef.current = false;
      return;
    }
    reload();
  }, [signedIn, reload]);

  const value: DealerContextValue = {
    token,
    me,
    state,
    blockedCode,
    reload,
    isOwner: me?.myRole === 'DEALER_OWNER',
    canCommitSpend: !!me && (me.myRole === 'DEALER_OWNER' || me.canCommitSpend),
    walletEnabled: me?.walletEnabled === true,
  };

  return <DealerContext.Provider value={value}>{children}</DealerContext.Provider>;
}

export function useDealer(): DealerContextValue {
  const ctx = useContext(DealerContext);
  if (!ctx) throw new Error('useDealer must be used within DealerProvider');
  return ctx;
}
